// Mednos Kosten — eigenes Overlay des Profil-Panels.
//
// Zeigt, was die LLM-Aufrufe der Plattform im laufenden Monat gekostet haben:
// Summe, Aufteilung nach App und Modell, Verlauf der letzten Tage. Die Zahlen
// kommen aus `/api/admin/llm-costs`, das der Auth-Server aus dem
// LiteLLM-Proxy aggregiert — der Browser sieht nie einen LiteLLM-Key.
//
// Wird von profile-panel.js lazy erzeugt, sobald ein Admin "Kosten" zum ersten
// Mal anklickt. Nur für Rolle `admin` erreichbar (der Button rendert sonst gar
// nicht); die Route prüft zusätzlich serverseitig.
//
// Kosten standen zwischenzeitlich als Block unter der Schlüsselliste im
// APIs-Tab der Admin-Konsole. Sie sind seit 07.09.2026 wieder ein eigener
// Menüpunkt: Schlüssel rotieren ist Wartung, Kosten sind ein Blick auf Zahlen —
// zwei Anlässe, die man nie zugleich hat. `renderKostenPane` bleibt der eine
// Ort, an dem der Inhalt entsteht; dieses Modul stellt nur den Rahmen darum.

import { authApi, ppLabel, ppHint } from './platform-api.js';
import { renderVerlaufChart, VERLAUF_CSS } from './llm-verlauf-chart.js';

const CSS = `
/* Look wie das Admin-Overlay (#1a1a1a, 12px, weicher Schatten). Eigener
   Klassen-Präfix, weil dieses Modul unabhängig vom Admin-Overlay geladen
   werden kann und dessen CSS dann nicht da wäre. */
.mednos-kosten-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5);
  z-index: 10010; display: none;
}
.mednos-kosten-backdrop.is-open { display: block; }
.mednos-kosten {
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%);
  width: min(46rem, 92vw); max-height: 86vh; overflow-y: auto;
  padding: 20px; display: none; flex-direction: column; gap: 16px;
  background: #1a1a1a; color: #fff;
  border: none; border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  z-index: 10011;
  font-family: 'Inter', system-ui, sans-serif;
}
.mednos-kosten.is-open { display: flex; }
.mednos-kosten { scrollbar-width: thin; scrollbar-color: #4a4a4a transparent; }
.mednos-kosten::-webkit-scrollbar { width: 8px; }
.mednos-kosten::-webkit-scrollbar-track { background: transparent; }
.mednos-kosten::-webkit-scrollbar-thumb {
  background: #4a4a4a; border-radius: 4px;
}
.mednos-kosten::-webkit-scrollbar-thumb:hover { background: #5f5f5f; }
.mednos-kosten__head { display: flex; align-items: center; gap: 12px; }
.mednos-kosten__title { font-size: 1rem; font-weight: 600; }
.mednos-kosten__close {
  margin-left: auto; flex-shrink: 0;
  width: 1.6rem; height: 1.6rem; padding: 0;
  border: none; background: transparent; color: #fff;
  font-size: 1.1rem; line-height: 1; cursor: pointer; border-radius: 0.5rem;
  transition: opacity 0.15s;
}
.mednos-kosten__close:hover { opacity: 0.65; }
.mednos-kosten__section { display: flex; flex-direction: column; gap: 8px; }
.mednos-kosten__summe { display: flex; align-items: baseline; gap: 12px; }
.mednos-kosten__gross { font-size: 1.8rem; font-weight: 600; letter-spacing: -0.02em; }
.mednos-kosten__vergleich { font-size: 0.78rem; color: #b0b0b0; }
/* Aufstellung: „Nach App" und „Nach Modell" stehen nebeneinander, jeder Posten
   ist eine quadratische Kachel mit Name und Monatsbetrag. Kein Prozentanteil —
   Reihenfolge und Betrag sagen dasselbe, nur genauer. Unter ~34rem Breite
   fallen die beiden Spalten untereinander. */
.mednos-kosten__paare { display: flex; flex-wrap: wrap; gap: 16px; }
.mednos-kosten__spalte {
  flex: 1 1 15rem; min-width: 0;
  display: flex; flex-direction: column; gap: 8px;
}
.mednos-kosten__gitter {
  display: grid; gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(6.2rem, 1fr));
}
.mednos-kosten__kachel {
  aspect-ratio: 1; min-width: 0;
  display: flex; flex-direction: column; justify-content: space-between;
  padding: 10px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}
.mednos-kosten__kachel-name {
  font-size: 0.72rem; line-height: 1.25; color: #b0b0b0;
  overflow-wrap: anywhere;
}
.mednos-kosten__kachel-betrag {
  font-size: 0.95rem; font-weight: 600; letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}
.mednos-kosten__inhalt { display: flex; flex-direction: column; gap: 16px; }
`;

/** US-Dollar mit vier Nachkommastellen unterhalb eines Cents — sonst zwei. */
function usd(betrag) {
  const n = Number(betrag) || 0;
  const stellen = n > 0 && n < 0.01 ? 4 : 2;
  return `${n.toFixed(stellen)} $`;
}

function fehlerText(status) {
  if (status === 401 || status === 403) return 'Keine Admin-Berechtigung (mehr).';
  if (status === 503) return 'Kosten derzeit nicht abrufbar — Proxy antwortet nicht.';
  return 'Kosten derzeit nicht abrufbar.';
}

/** Eine Kachel der Aufschlüsselung. Alles per textContent, nie per innerHTML. */
function kachel(name, betrag) {
  const el = document.createElement('div');
  el.className = 'mednos-kosten__kachel';
  const n = document.createElement('span');
  n.className = 'mednos-kosten__kachel-name';
  n.textContent = name;
  const b = document.createElement('span');
  b.className = 'mednos-kosten__kachel-betrag';
  b.textContent = usd(betrag);
  el.append(n, b);
  return el;
}

/** Beschriftete Spalte mit einem Kachelgitter — leer bleibt sie ein Hinweis. */
function spalte(el, titel, posten, leerText) {
  if (!Array.isArray(posten) || !posten.length) {
    el.replaceChildren(ppLabel(titel), ppHint(leerText));
    return;
  }
  const gitter = document.createElement('div');
  gitter.className = 'mednos-kosten__gitter';
  gitter.append(...posten.map((p) => kachel(p.name, p.usd)));
  el.replaceChildren(ppLabel(titel), gitter);
}

async function rendern(box, teile) {
  const r = await authApi('/api/admin/llm-costs');
  if (r.status !== 200 || !r.body) {
    teile.summe.replaceChildren(ppHint(fehlerText(r.status)));
    teile.apps.replaceChildren();
    teile.modelle.replaceChildren();
    return;
  }
  const d = r.body;
  const summe = Number(d.summe_usd) || 0;

  const gross = document.createElement('span');
  gross.className = 'mednos-kosten__gross';
  gross.textContent = usd(summe);
  const vergleich = document.createElement('span');
  vergleich.className = 'mednos-kosten__vergleich';
  vergleich.textContent =
    d.vormonat_usd === null || d.vormonat_usd === undefined
      ? `${d.monat} · Vormonat unbekannt`
      : `${d.monat} · Vormonat ${usd(d.vormonat_usd)}`;
  const kopf = document.createElement('div');
  kopf.className = 'mednos-kosten__summe';
  kopf.append(gross, vergleich);
  teile.summe.replaceChildren(kopf);

  spalte(teile.apps, 'Nach App', d.pro_app, 'Noch keine Calls in diesem Monat.');
  spalte(teile.modelle, 'Nach Modell', d.pro_modell, 'Noch keine Calls in diesem Monat.');
}

let stylesEingehaengt = false;
function stelleStylesSicher() {
  if (stylesEingehaengt) return;
  const style = document.createElement('style');
  // Beides, sonst stuenden die Balken des Verlaufsdiagramms ohne Regeln da: der
  // Chart bringt sein CSS mit, dieses Modul haengt es fuer beide Aufrufwege ein.
  // `.profile-panel__*` (ueber ppLabel/ppHint) stammt aus dem Panel-CSS und ist
  // da, weil dieses Modul ausschliesslich aus initProfilePanel heraus laeuft.
  style.textContent = KOSTEN_CSS;
  document.head.appendChild(style);
  stylesEingehaengt = true;
}

/** Baut Backdrop + Overlay einmalig und gibt `{ open }` zurück. */
export function initLlmCostsOverlay() {
  stelleStylesSicher();

  const backdrop = document.createElement('div');
  backdrop.className = 'mednos-kosten-backdrop';
  const box = document.createElement('div');
  box.className = 'mednos-kosten';

  const head = document.createElement('div');
  head.className = 'mednos-kosten__head';
  const title = document.createElement('span');
  title.className = 'mednos-kosten__title';
  title.textContent = 'Kosten';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'mednos-kosten__close';
  closeBtn.setAttribute('aria-label', 'Schließen');
  closeBtn.textContent = '✕';
  head.append(title, closeBtn);

  // Der Inhalt bekommt einen eigenen Knoten: renderKostenPane raeumt seinen
  // Container per replaceChildren leer, und der Kopf soll das ueberleben.
  // Die erklaerende Fussnote (Erfassungsgrenzen des Proxys) ist seit 10.09.2026
  // auf Wunsch entfernt.
  const inhalt = document.createElement('div');
  inhalt.className = 'mednos-kosten__inhalt';

  box.append(head, inhalt);
  document.body.append(backdrop, box);

  function close() {
    backdrop.classList.remove('is-open');
    box.classList.remove('is-open');
  }
  function open() {
    backdrop.classList.add('is-open');
    box.classList.add('is-open');
    // Bei jedem Oeffnen neu laden — die Zahlen wachsen waehrend der Sitzung.
    renderKostenPane(inhalt);
  }

  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && box.classList.contains('is-open')) close();
  });

  return { open };
}

/**
 * Baut den Kosten-Block in `container` und startet das Laden — der eine Ort, an dem
 * der Inhalt entsteht. `initLlmCostsOverlay` stellt nur den Rahmen darum.
 */
export function renderKostenPane(container) {
  const teile = {
    summe: document.createElement('div'),
    apps: document.createElement('div'),
    modelle: document.createElement('div'),
  };
  teile.summe.className = 'mednos-kosten__section';
  teile.apps.className = 'mednos-kosten__spalte';
  teile.modelle.className = 'mednos-kosten__spalte';

  // Beide Aufstellungen teilen sich eine Zeile; erst wenn kein Platz mehr ist,
  // rutscht „Nach Modell" unter „Nach App".
  const paare = document.createElement('div');
  paare.className = 'mednos-kosten__paare';
  paare.append(teile.apps, teile.modelle);

  const verlauf = document.createElement('div');

  container.replaceChildren(teile.summe, paare, verlauf);
  teile.summe.replaceChildren(ppHint('Lade…'));
  rendern(container, teile);
  renderVerlaufChart(verlauf);
}

/** CSS dieses Moduls, damit die Konsole es einmalig mit einhaengen kann. */
export const KOSTEN_CSS = [CSS, VERLAUF_CSS].join('\n');
